import { ManagedRuntimeColumn, ResolvedColumnWidths } from './types';

const clampWidth = (px: number, minPx: number, maxPx?: number): number =>
    Math.min(Math.max(px, minPx), maxPx ?? Number.POSITIVE_INFINITY);

const NO_OVERRIDES: Record<string, number> = {};

/**
 * The width a column is frozen at before any distribution — its declared `fixedPx`, else the user's
 * persisted resize ratio against the live container — or `undefined` for a column the weights still
 * size. `fixedPx` wins: that size is the column's own, not the user's to drag.
 *
 * Shared by both width paths on purpose, so what a persisted ratio means in pixels has one
 * definition. Rounding, clamping and the precedence between the two are all here.
 */
function frozenWidth(
    column: ManagedRuntimeColumn,
    containerPx: number,
    overrides: Record<string, number>
): number | undefined {
    if (column.fixedPx !== undefined) {
        return Math.round(column.fixedPx);
    }
    const override = overrides[column.id];
    return override !== undefined
        ? clampWidth(Math.round(override * containerPx), column.minPx, column.maxPx)
        : undefined;
}

/**
 * Weighted distribution: `fixedPx` columns and user-overridden columns are frozen first, the
 * remaining space is split across flex columns by `widthWeight`, iteratively freezing any column
 * that falls below `minPx` (or above `maxPx`) until the distribution is stable. At large container
 * widths the columns fill it exactly; when the minimum widths don't fit, the total exceeds the
 * container and the table overflows into its horizontal scroll container — columns never collapse
 * below `minPx`.
 */
function distributeByWeight(
    visibleColumns: ManagedRuntimeColumn[],
    containerPx: number,
    overrides: Record<string, number>
): Record<string, number> {
    const widths: Record<string, number> = {};
    // Float widths of columns still participating in flex distribution.
    const flexWidths = new Map<string, number>();

    let frozenTotalPx = 0;
    const flexColumns: ManagedRuntimeColumn[] = [];
    for (const column of visibleColumns) {
        const frozen = frozenWidth(column, containerPx, overrides);
        if (frozen !== undefined) {
            widths[column.id] = frozen;
            frozenTotalPx += frozen;
        } else {
            flexColumns.push(column);
        }
    }

    // Iterative distribution: each pass divides the remaining space by weight and freezes
    // the columns that violate their min/max; terminates in ≤ |flexColumns| passes.
    let unfrozen = flexColumns;
    let availablePx = containerPx - frozenTotalPx;
    for (;;) {
        const totalWeight = unfrozen.reduce((sum, column) => sum + column.widthWeight, 0);
        // Every column in a pass is evaluated against the same pool snapshot; deducting
        // freezes mid-pass would shrink the pool for the columns visited later, making
        // the outcome depend on column order (e.g. an early maxPx freeze would deny a
        // later minPx column the space it should inherit on the next pass).
        const passAvailablePx = Math.max(availablePx, 0);
        const stillFlex: ManagedRuntimeColumn[] = [];
        for (const column of unfrozen) {
            const ideal = totalWeight > 0 ? (passAvailablePx * column.widthWeight) / totalWeight : 0;
            const clamped = clampWidth(ideal, column.minPx, column.maxPx);
            if (clamped === ideal) {
                stillFlex.push(column);
            } else {
                widths[column.id] = Math.round(clamped);
                availablePx -= widths[column.id];
            }
        }
        if (stillFlex.length === unfrozen.length || stillFlex.length === 0) {
            unfrozen = stillFlex;
            break;
        }
        unfrozen = stillFlex;
    }

    if (unfrozen.length > 0) {
        // The table fits: floor the flex widths, then hand the lost fractional pixels back
        // (largest remainder first) so the widths sum exactly to the container width.
        const totalWeight = unfrozen.reduce((sum, column) => sum + column.widthWeight, 0);
        const spentOnFrozen = visibleColumns.reduce(
            (sum, c) => (widths[c.id] !== undefined ? sum + widths[c.id] : sum),
            0
        );
        const flexAvailable = containerPx - spentOnFrozen;
        for (const column of unfrozen) {
            flexWidths.set(column.id, (flexAvailable * column.widthWeight) / totalWeight);
        }
        const byRemainder = [...unfrozen].sort(
            (a, b) => (flexWidths.get(b.id)! % 1) - (flexWidths.get(a.id)! % 1) || b.widthWeight - a.widthWeight
        );
        for (const column of unfrozen) {
            widths[column.id] = Math.floor(flexWidths.get(column.id)!);
        }
        let remainderPx = containerPx - visibleColumns.reduce((sum, c) => sum + widths[c.id], 0);
        for (const column of byRemainder) {
            if (remainderPx <= 0) break;
            widths[column.id] += 1;
            remainderPx -= 1;
        }
    }

    return widths;
}

/**
 * Resolve per-column pixel widths from the live container width.
 *
 * Widths come from {@link distributeByWeight} — a flexbox-style distribution of the container
 * across the columns' `widthWeight`s, honouring `fixedPx`, `minPx`, `maxPx` and the user's resize
 * overrides.
 *
 * An `absorbSlack` column redirects what a RESIZE does. Every other column keeps the width the
 * weights alone give it — the layout as if nothing had been dragged — and the absorber takes
 * whatever is then left over, so narrowing a column hands its space to that one column instead of
 * spreading it across all of them, and widening one takes the space back from it. With no overrides
 * in play the two agree exactly, so the default layout is the weighted one either way. The absorber
 * is skipped while it is ITSELF overridden: the user has stated that column's width, and the space
 * their drag freed has nowhere else to go but back into the weighted distribution. Same fallback when
 * a `maxPx` on the absorber would refuse part of the leftover, since only the weights can place what
 * it will not take.
 *
 * Sticky offsets are prefix sums over the leading run of `sticky` columns; a `sticky`
 * column that doesn't belong to that prefix is ignored here (the caller dev-warns).
 */
export const resolveColumnWidths = (
    visibleColumns: ManagedRuntimeColumn[],
    containerPx: number,
    overrides: Record<string, number>
): ResolvedColumnWidths => {
    const absorber = visibleColumns.find(
        (column) => column.absorbSlack && column.fixedPx === undefined && overrides[column.id] === undefined
    );

    let widths: Record<string, number> | undefined;
    if (absorber !== undefined) {
        // The unresized layout is what the other columns hold on to; the absorber is then sized from
        // what they leave, floored at its own minPx so a container too narrow for the lot overflows
        // into horizontal scroll rather than crushing it.
        const natural = distributeByWeight(visibleColumns, containerPx, NO_OVERRIDES);
        const absorbed: Record<string, number> = {};
        let spentPx = 0;
        for (const column of visibleColumns) {
            if (column.id === absorber.id) continue;
            absorbed[column.id] = frozenWidth(column, containerPx, overrides) ?? natural[column.id];
            spentPx += absorbed[column.id];
        }
        const leftoverPx = containerPx - spentPx;
        // A maxPx ceiling on the absorber leaves width nobody has been given: unlike the weighted
        // pass, this one does not loop, so the refused pixels would simply go unpainted — and the
        // table is laid out by its own resolved widths, so that shows as a bare strip past the last
        // column. The weights can place them, so hand the whole layout back to them.
        if (absorber.maxPx === undefined || leftoverPx <= absorber.maxPx) {
            absorbed[absorber.id] = clampWidth(leftoverPx, absorber.minPx, absorber.maxPx);
            widths = absorbed;
        }
    }
    widths ??= distributeByWeight(visibleColumns, containerPx, overrides);

    const tableMinPx = visibleColumns.reduce((sum, column) => sum + widths[column.id], 0);

    const stickyLefts: Record<string, number> = {};
    let left = 0;
    for (const column of visibleColumns) {
        if (!column.sticky) break;
        stickyLefts[column.id] = left;
        left += widths[column.id];
    }

    return { widths, tableMinPx, stickyLefts };
};
